// frontend/src/components/LikesButton.js
// Like/unlike a published Look. Authenticated users are keyed by their token;
// guests (device-token) can like too — zero signup friction from shared links
// (W2 adoption decision §13.1). Rate-limited and deduped server-side.

import React, { useState, useEffect } from "react";
import { FaHeart, FaRegHeart } from "react-icons/fa";

// Same device-token used for view dedup — stable per browser.
function getViewerId() {
  const storageKey = "ogestyle_look_viewer_id";
  let viewerId = localStorage.getItem(storageKey);
  if (!viewerId) {
    const randomPart =
      typeof crypto !== "undefined" && typeof crypto.randomUUID === "function"
        ? crypto.randomUUID()
        : `${Date.now()}-${Math.random().toString(36).slice(2, 12)}`;
    viewerId = `lv-${randomPart}`;
    localStorage.setItem(storageKey, viewerId);
  }
  return viewerId;
}

function LikesButton({ lookId, apiBase, initialLikes = 0 }) {
  const [liked, setLiked] = useState(false);
  const [count, setCount] = useState(initialLikes);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");

  const token = localStorage.getItem("ogestyle_token");
  // Authenticated requests carry the bearer token; guest requests carry the
  // device token so the server can dedupe and rate-limit per viewer.
  const headers = token
    ? { Authorization: `Bearer ${token}` }
    : { "x-viewer-id": getViewerId() };

  const load = async () => {
    try {
      const res = await fetch(`${apiBase}/api/looks/${lookId}/engagement`, {
        headers,
      });
      const data = await res.json();
      if (res.ok) {
        if (typeof data.likeCount === "number") setCount(data.likeCount);
        setLiked(!!data.likedByMe);
      }
    } catch (err) {
      // ignore
    }
  };

  useEffect(() => {
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [lookId]);

  const toggle = async () => {
    setError("");
    setBusy(true);
    try {
      const res = await fetch(`${apiBase}/api/looks/${lookId}/like`, {
        method: liked ? "DELETE" : "POST",
        headers,
      });
      const data = await res.json();
      if (res.ok) {
        setLiked(!!data.liked);
        if (typeof data.likeCount === "number") setCount(data.likeCount);
      } else if (res.status === 401) {
        setError("Your session expired — please log in again.");
      } else if (res.status === 409) {
        setError(data?.message || "You can't like this look.");
      } else if (res.status === 429) {
        setError(data?.message || "You've liked too many looks — try again later.");
      } else {
        setError(data?.message || "Unable to update like.");
      }
    } catch (err) {
      setError("Unable to update like. Check your connection.");
    } finally {
      setBusy(false);
    }
  };

  return (
    <span
      style={{
        display: "inline-flex",
        flexDirection: "column",
        alignItems: "center",
        gap: "4px",
      }}
    >
      <button
        type="button"
        onClick={toggle}
        disabled={busy}
        aria-pressed={liked}
        title={liked ? "Unlike" : "Like"}
        aria-label={liked ? "Unlike this look" : "Like this look"}
        style={{
          display: "inline-flex",
          alignItems: "center",
          gap: "6px",
          padding: "6px 12px",
          borderRadius: "999px",
          border: liked ? "1px solid #fecdd3" : "1px solid #e5e7eb",
          background: liked ? "#fef2f2" : "#fff",
          color: liked ? "#e0245e" : COLOR_LIKE,
          cursor: "pointer",
          fontSize: "13px",
          fontWeight: 600,
          // Ensure a tap reliably fires a click on mobile (removes the
          // touch delay / double-tap zoom that can swallow taps on Firefox
          // Android and iOS Safari).
          touchAction: "manipulation",
          WebkitTapHighlightColor: "transparent",
        }}
      >
        {liked ? <FaHeart /> : <FaRegHeart />} {count}
      </button>
      {error ? (
        <span
          style={{
            fontSize: "11px",
            color: "#b91c1c",
            textAlign: "center",
            maxWidth: "170px",
          }}
        >
          {error}
        </span>
      ) : null}
    </span>
  );
}

const COLOR_LIKE = "#6b7280";

export default LikesButton;
